import { useEffect, useMemo, useRef, useState } from 'react'
import { Array as A, Effect, Option as O, pipe } from 'effect'
import { getOrdinalSuffix } from 'src/utils/getOrdinalSuffix'
import { formatDate } from 'src/utils/formatDate'
import type { AuthenticatedPlayer } from 'src/types/domain/Player'
import FlagCheckeredIcon from 'src/app/assets/icons/ico-flag-checkered.svg'
import AngleUpIcon from 'src/app/assets/icons/ico-angle-up.svg'
import CopyIcon from 'src/app/assets/icons/ico-copy.svg'
import CheckIcon from 'src/app/assets/icons/ico-check.svg'
import type { Bet } from 'src/types/domain/Bet'
import { getCurrencySymbol } from 'src/utils/getCurrencySymbol'
import { BetClassNameMap, getVisualPotentialPayout } from 'src/utils/betUtils'
import type { MarbleGame } from 'src/types/domain/Game'
import type { Marble } from 'src/types/domain/Marble'
import type { Race } from 'src/types/domain/Race'
import {
  isRaceSettledEvent,
  isRaceEndedEvent,
  isRaceStartedEvent,
  type RaceEvent,
  type RaceSettledEvent
} from 'src/types/domain/RaceEvent'
import { displayMoney } from 'src/utils/displayAmount'
import { roundAmount } from 'src/utils/roundAmount'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentLanguage, useGetGameConfig } from 'src/store/game'
import { isKnownGameConfig } from 'src/types/domain/GameConfig'
import { getRacesByIds } from 'src/services/supabase/queries'
import { getRaceRoundId } from 'src/utils/getRaceRoundId'
import { useFormatTimeDifference } from 'src/utils/formatTimeDifference'
import { BetTypeLabel } from 'src/components/BetTypeLabel/BetTypeLabel'
import { HISTORY } from 'src/config'
import { isCrashGameType, isCCTVGameType, isBirdieGameType } from 'src/utils/gameTypeUtils'
import { BirdieBetHistoryRows } from 'src/components/BirdieBoard/BirdieBetHistoryRows'
import { groupBetsByRound, type BirdieBetRound } from 'src/components/BirdieBoard/betHistoryRounds'
import { getTranslatedMarbleColor } from 'src/services/i18n/utils/gameTranslations'
import { MarbleItem } from 'src/components/MarbleItem'
import {
  CrashRoundModal,
  hasRoundFairness,
  resolveModalRace
} from 'src/components/CrashMultiplierHistory'
import { BetHistorySelectedMarbles } from './BetHistorySelectedMarbles'
import { BetHistoryMarbleWrapper } from './BetHistoryMarbleWrapper'

type Props = Readonly<{
  game: MarbleGame
  bets: Bet[]
  player: AuthenticatedPlayer
  raceTrackName?: string
}>

const getRaceForBet = (races: Race[], bet: Bet) => races.find((race) => race.uuid === bet.raceId)

/** Open the full round on support.marbles.xyz — clip replay, the
 *  provable-fairness card, and one-click independent verify. */
const supportLookupUrl = (bet: Bet) =>
  `https://support.marbles.xyz/lookup?gameId=${encodeURIComponent(
    bet.raceConfigurationId
  )}&roundId=${encodeURIComponent(bet.raceId)}&betId=${encodeURIComponent(bet.id)}`

/** Stable identity for every non-Birdie game, so the memo below never churns. */
const NO_BIRDIE_ROUNDS: BirdieBetRound[] = []

/**
 * Helper component to render crash game bet history result
 * Shows: cashout multiplier if cashed out, crash multiplier if lost, or "In Play" if pending
 */
// The round's realized crash point, from the race_ended event. Shared by the
// collapsed "CRASHED @ x.xx" badge and the expanded Multiplier row so the two
// can never disagree.
function crashMultiplierFor(race: Race | undefined): number | undefined {
  const raceEndedEvent = race?.raceEvents.find((e) => e.eventType === 'race_ended')
  return raceEndedEvent && isRaceEndedEvent(raceEndedEvent)
    ? raceEndedEvent.payload?.crash_multiplier
    : undefined
}

function CrashBetHistoryResult({
  bet,
  race,
  raceEnded,
  t
}: {
  bet: Bet
  race: Race | undefined
  raceEnded: boolean
  t: (key: string) => string
}) {
  const crashMultiplier = crashMultiplierFor(race)

  // Calculate cashout multiplier from bet data
  // 🚨 NEVER derive a crash multiplier from potentialPayout. For a crash bet
  // potentialPayout is the operator LIABILITY CEILING, not stake x anything
  // (CrashPotentialPayout ignores the stake because the multiplier is uncapped),
  // so the ratio is meaningless — a 4.20 stake against a 250,000 ceiling renders
  // 59523.8095x. Crash uses the real cashout point; the payout fallback stays for
  // marble/counting, whose potentialPayout genuinely is stake x fixed odds.
  const getCashoutMultiplier = () => {
    if (bet.cashoutMultiplier) return bet.cashoutMultiplier
    // This component only renders for crash rounds, so the payout-ratio fallback
    // below would be the ceiling. Prefer the round's realized crash point.
    if (crashMultiplier) return crashMultiplier
    if (bet.betAmount > 0 && bet.potentialPayout > 0) {
      return bet.potentialPayout / bet.betAmount
    }
    return 0
  }

  if (bet.status === 'cashed_out' || bet.status === 'won') {
    // Cashed out successfully
    const multiplier = getCashoutMultiplier()
    return (
      <div className="crash-bet-history-result">
        <span className="crash-cashout-badge">
          {t('Cashed Out')} {multiplier.toFixed(2)}x
        </span>
      </div>
    )
  }

  if (bet.status === 'lost') {
    // Lost - show crash multiplier if available
    return (
      <div className="crash-bet-history-result">
        <span className="crash-lost-badge">
          {crashMultiplier ? `${t('Crashed')} @ ${crashMultiplier.toFixed(2)}x` : t('Crashed')}
        </span>
      </div>
    )
  }

  if (raceEnded) {
    // Race ended but bet still pending (shouldn't normally happen)
    return (
      <div className="crash-bet-history-result">
        <span className="crash-lost-badge">
          {crashMultiplier ? `${crashMultiplier.toFixed(2)}x` : t('Crashed')}
        </span>
      </div>
    )
  }

  // Still in play
  return (
    <div className="crash-bet-history-result">
      <span className="crash-pending-badge">{t('In Play')}</span>
    </div>
  )
}

export default function BetHistoryPane({ bets, game, raceTrackName }: Props) {
  const currentLang = useGetCurrentLanguage()
  const { t } = useClientSideTranslation(currentLang)
  const gameConfig = useGetGameConfig()

  // Cross-reference each picked marble to the market it belongs to
  // (main vs configured side-bet). The bet aggregate doesn't carry
  // marketId on the wire, but the picked marble's option ID is unique
  // across all markets in the chip-placement model so a single map
  // built off `gameConfig.markets` resolves both lookups: the bet's
  // market label (for the row title) and which `payload.results` /
  // `payload.marketResults[marketId]` to read for settlement.
  const marbleMarketMap = useMemo(() => {
    const map = new Map<string, { marketId: string; marketLabel: string; marketType: string }>()
    if (isKnownGameConfig(gameConfig) && gameConfig.markets) {
      for (const market of gameConfig.markets) {
        for (const option of market.options) {
          map.set(option.id, {
            marketId: market.id,
            marketLabel: market.label,
            marketType: market.type
          })
        }
      }
    }
    return map
  }, [gameConfig])

  const [expandedBetId, setExpandedBetId] = useState<string | null>(null)
  // A bet row's "Provably fair" affordance opens the same CrashRoundModal the
  // history pills open — against the Supabase-fetched Race (which carries
  // race_ready's commitment + race_settled's reveal), so it works long after
  // the in-memory store pruned the round. Offered for any round that published
  // fairness artifacts, not per game family: the flat rows gate on
  // `hasRoundFairness` below, and the Birdie rows (B33) get the same setter as
  // `onOpenFairness` and gate on it themselves — the modal reads the family
  // off the reveal and mounts the Birdie body for a Birdie round.
  const [fairnessRaceId, setFairnessRaceId] = useState<string | null>(null)
  const [copiedStates, setCopiedStates] = useState<Record<string, boolean>>({})
  const [races, setRaces] = useState<Race[]>([])
  const [isLoading, setIsLoading] = useState<boolean>(false)
  // Pagination is a COUNT derived against the live `bets` prop, not a
  // snapshot of pages. The old shape (historicalBets state + a reset
  // effect keyed on [bets]) collapsed the list back to page 1 on every
  // store tick — the selector hands us a fresh array identity constantly
  // during live rounds — which shrank the scroll container and slammed
  // the pane back to the top right after "Load More" (QA: history jumps).
  const [visibleCount, setVisibleCount] = useState<number>(HISTORY.BETS_PER_PAGE)

  const formatTimeDifference = useFormatTimeDifference()

  const sortedBets = useMemo(
    () =>
      [...bets].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()),
    [bets]
  )
  // ── B26: Birdie's history is ONE ENTRY PER ROUND ────────────────────
  //
  // A player backs several options in one round, and the flat list files each
  // chip as its own unrelated entry. Grouping is a pure transform over the
  // list already in memory (`BirdieBoard/betHistoryRounds.ts`) — the store
  // hands over every bet in one push, so no round can straddle a fetch
  // boundary — which leaves exactly two things to change here: a page counts
  // ROUNDS, and the list renders `<BirdieBetHistoryRows>`.
  //
  // Every other game is untouched: `sortedBets.slice(0, visibleCount)` below
  // is the expression `historicalBets` always was, and `hasMoreBets` its
  // comparison.
  const isBirdie = isBirdieGameType(game.track.gameType)
  const birdieRounds = useMemo(
    () => (isBirdie ? groupBetsByRound(sortedBets) : NO_BIRDIE_ROUNDS),
    [isBirdie, sortedBets]
  )
  const visibleBirdieRounds = useMemo(
    () => birdieRounds.slice(0, visibleCount),
    [birdieRounds, visibleCount]
  )
  const historicalBets = useMemo(() => {
    if (!isBirdie) return sortedBets.slice(0, visibleCount)
    // The Supabase race-hydration effect below reads this, so a Birdie page's
    // fetch window is every bet on the VISIBLE ROUNDS — not the first
    // `visibleCount` bets, which would leave the last round's races unloaded.
    const onVisibleRounds: Bet[] = []
    for (const round of visibleBirdieRounds) {
      for (const bet of round.bets) onVisibleRounds.push(bet)
    }
    return onVisibleRounds
  }, [isBirdie, sortedBets, visibleBirdieRounds, visibleCount])
  const hasMoreBets = isBirdie
    ? birdieRounds.length > visibleBirdieRounds.length
    : sortedBets.length > historicalBets.length

  // Fresh game → fresh first page (the pane instance survives game swaps).
  useEffect(() => {
    setVisibleCount(HISTORY.BETS_PER_PAGE)
  }, [game.id])

  // Supabase race hydration for whatever is visible. Each race id is
  // normally requested once per mount — store ticks and page growth only
  // fetch NEW ids — but a race first fetched mid-round is UNSETTLED
  // (no crash point, no revealed seed), so once its bet resolves the id
  // becomes eligible again (throttled) until the settled row lands.
  // Settled races replace their unsettled cache entry below.
  const requestedRaceIdsRef = useRef<Map<string, number>>(new Map())
  useEffect(() => {
    const now = Date.now()
    const RETRY_MS = 10_000
    const isSettledInCache = (id: string) =>
      races.some((race) => race.uuid === id && race.raceEvents.some(isRaceSettledEvent))
    // DISTINCT round ids. The filter runs per BET, and B26's Birdie window is
    // every bet on the visible rounds — up to twelve chips on one round — so
    // without the Set the same id was pushed once per chip and `in.(...)`
    // carried ~120 repeats of ten rounds. Correct and cheaper on every path
    // (a flat page can hold two bets on one round too), and it restores
    // `limit` below to the true distinct count.
    const ids = Array.from(
      new Set(
        historicalBets
          .filter((bet) => Boolean(bet.raceId))
          .filter((bet) => {
            const last = requestedRaceIdsRef.current.get(bet.raceId)
            if (last === undefined) return true
            const betResolved = ['won', 'lost', 'cashed_out', 'cancelled'].includes(bet.status)
            return betResolved && !isSettledInCache(bet.raceId) && now - last > RETRY_MS
          })
          .map((bet) => bet.raceId)
      )
    )
    if (ids.length === 0) return
    ids.forEach((id) => requestedRaceIdsRef.current.set(id, now))

    setIsLoading(true)
    void pipe(
      // `limit` is not optional in practice: getRacesByIds defaults it to 10,
      // so asking for more than ten specific ids silently returned the first
      // ten and the rest never hydrated their round.
      getRacesByIds({ ids, limit: ids.length, order: 'desc', orderBy: 'created_at' }),
      Effect.tap((results) => {
        setRaces((prevRaces) => {
          // Refetched rounds REPLACE their stale (unsettled) cache entry so
          // the settled events actually land; unknown rounds append.
          const byId = new Map(prevRaces.map((race) => [race.uuid, race]))
          for (const race of results) byId.set(race.uuid, race)
          return [...byId.values()]
        })
      }),
      Effect.runPromise
    ).finally(() => {
      setIsLoading(false)
    })
  }, [historicalBets, races])

  const loadMoreBets = () => {
    setVisibleCount((count) => count + HISTORY.BETS_PER_PAGE)
  }

  const isRaceEnded = (race?: Race) => {
    return race?.raceEvents.some((event: RaceEvent) => event.eventType === 'race_settled')
  }

  const handleEntryClick = (betId: string) => {
    setExpandedBetId(expandedBetId === betId ? null : betId)
  }

  const handleCopy = async (value: string, key: string) => {
    try {
      // First try the modern Clipboard API
      try {
        await navigator.clipboard.writeText(value)
        setCopiedStates((prev) => ({ ...prev, [key]: true }))
      } catch (clipboardError) {
        // Fallback to execCommand if Clipboard API fails
        const textarea = document.createElement('textarea')
        textarea.value = value
        textarea.style.position = 'fixed'
        textarea.style.opacity = '0'
        textarea.style.pointerEvents = 'none'
        document.body.appendChild(textarea)

        textarea.focus()
        textarea.select()

        try {
          const successful = document.execCommand('copy')
          if (!successful) throw new Error('Copy command was unsuccessful')
          setCopiedStates((prev) => ({ ...prev, [key]: true }))
        } finally {
          document.body.removeChild(textarea)
        }
      }

      // Set timeout to reset the "Copied!" state
      setTimeout(() => {
        setCopiedStates((prev) => ({ ...prev, [key]: false }))
      }, 2000)
    } catch (err) {
      setCopiedStates((prev) => ({ ...prev, [key]: false }))
    }
  }

  const renderBetMarbles = (
    bet: Bet,
    raceSettled: RaceSettledEvent | undefined,
    isRaceEnded: boolean
  ) => {
    const betInExactOrder = ['StraightForecast', 'Tricast'].includes(bet.betDetails.type)
    const selectedMarbles = bet.betDetails.selectedItems
      .map((marbleId) => game.marbles.find((m: Marble) => m.uuid === marbleId))
      .filter((m: Marble | undefined) => m !== undefined)
    if (selectedMarbles.length === 0) return null
    if (bet.betDetails.type === 'PickWinner') {
      return selectedMarbles.map((marble) => {
        const { position, isWinningMarble } = pipe(
          O.fromNullable(raceSettled),
          O.map((result) => result.payload.results),
          O.flatMap((results) =>
            pipe(
              results,
              A.findFirst((result) => result.marbleId === marble.uuid),
              O.map((result) => ({
                position: result.position,
                positionSuffix: getOrdinalSuffix(Number(result.position)),
                isWinningMarble: isRaceEnded && result.position === 1
              }))
            )
          ),
          O.getOrElse(() => ({
            position: 0,
            positionSuffix: '',
            isWinningMarble: false
          }))
        )

        return (
          <BetHistoryMarbleWrapper
            isWinningMarble={isWinningMarble}
            key={marble.uuid}
            marble={marble}
            position={position}
          />
        )
      })
    }

    return (
      <div className="choose-marbles-order show-single-position-compact">
        {selectedMarbles.map((marble) => {
          const position = bet.betDetails.selectedItems.indexOf(marble.uuid) + 1

          return (
            <BetHistorySelectedMarbles
              exactOrder={betInExactOrder}
              isRaceEnded={isRaceEnded}
              key={marble.uuid}
              length={selectedMarbles.length}
              marble={marble}
              position={position}
            />
          )
        })}
      </div>
    )
  }

  return (
    <div className="race-history-sidebar">
      <h3>{raceTrackName}</h3>
      <div className="std-table three-rows-multiple-columns">
        <div className="std-table__inner">
          <div className="std-table__inner-body no-max-height">
            {/* THE ONE GATED BRANCH (B26). Birdie renders round entries that
                open into their bets; every other game renders the flat list
                below, unchanged. The row is a Birdie-owned component living
                under `BirdieBoard/`, exactly as `<BirdieRoundRows>` is for the
                past-races sidebar — one surface per job, and Birdie is a
                rendering inside it. */}
            {isBirdie ? (
              <BirdieBetHistoryRows
                copiedStates={copiedStates}
                formatBetTime={(timestamp) => formatDate(timestamp, currentLang)}
                formatTimeAgo={(timestamp) => formatTimeDifference(timestamp)}
                marbles={game.marbles}
                onCopy={(value, key) => {
                  void handleCopy(value, key)
                }}
                onOpenFairness={setFairnessRaceId}
                races={races}
                rounds={visibleBirdieRounds}
                verifyUrl={supportLookupUrl}
              />
            ) : historicalBets.length > 0 ? (
              historicalBets.map((bet: Bet) => {
                const race = getRaceForBet(races, bet)
                const raceSettledEvent = pipe(
                  O.fromNullable(race),
                  O.flatMap((race) => A.findFirst(race.raceEvents, isRaceSettledEvent)),
                  O.getOrUndefined
                )
                const raceEnded = isRaceEnded(race) ?? false
                const isExpanded = expandedBetId === bet.id

                return (
                  <div
                    className={`entry entry-expandable ${BetClassNameMap[bet.status]} ${
                      isExpanded ? 'expanded' : ''
                    }`}
                    key={bet.id}
                    onClick={() => {
                      handleEntryClick(bet.id)
                    }}
                  >
                    <div className="expand-icon">
                      <AngleUpIcon />
                    </div>
                    <div className="top-bar">
                      <div className="top-bar_entry-row">
                        <span className="race-id">
                          <FlagCheckeredIcon />{' '}
                          {race && getRaceRoundId(race.round, game.track.shortName)}
                        </span>
                        <span className="time-ago">{formatTimeDifference(bet.createdAt)}</span>
                      </div>
                      <div className="top-bar_entry-row">
                        <div className="amount">
                          <span className="amount">
                            {getCurrencySymbol(bet.currency)}
                            {displayMoney(bet.betAmount.toString())}
                          </span>
                        </div>
                        <div className="label">{t('Bet Amount')}</div>
                      </div>
                      {Boolean(bet.status === 'won') && (
                        <div className="top-bar_entry-row">
                          <div className="amount">
                            <span className="amount">
                              {getCurrencySymbol(bet.currency)}
                              {getVisualPotentialPayout(bet, bet.status, isCrashGameType(game.track.gameType))}
                            </span>
                          </div>
                          <div className="label">{t('Won Amount')}</div>
                        </div>
                      )}

                      {Boolean(bet.status === 'cashed_out') && (
                        <div className="top-bar_entry-row">
                          <div className="amount">
                            <span className="amount">
                              {getCurrencySymbol(bet.currency)}
                              {getVisualPotentialPayout(bet, bet.status, isCrashGameType(game.track.gameType))}
                            </span>
                          </div>
                          <div className="label">{t('Cashed Out')}</div>
                        </div>
                      )}

                      {Boolean(bet.status === 'lost') && <div className="amount">{t('Lost')}</div>}

                      {Boolean(bet.status === 'pending') && (
                        <div className="amount">{t('Pending')}</div>
                      )}
                    </div>
                    <div className={`expanded-info-bar ${isExpanded ? 'visible' : ''}`}>
                      <div className="info-entry">
                        {/* v47: Race → Round, matching the Game Info panel's
                            global rename (all games). */}
                        <span className="label">{t('Round ID')}:</span>
                        <span className="value">#{bet.raceId}</span>
                        <button
                          className="default-btn copy-btn"
                          title={copiedStates[`race-${bet.id}`] ? t('Copied!') : t('Copy')}
                          aria-label={copiedStates[`race-${bet.id}`] ? t('Copied!') : t('Copy')}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleCopy(bet.raceId, `race-${bet.id}`)
                          }}
                        >
                          {copiedStates[`race-${bet.id}`] ? (
                            <CheckIcon className="copy-btn__icon copy-btn__icon--done" />
                          ) : (
                            <CopyIcon className="copy-btn__icon" />
                          )}
                        </button>
                      </div>
                      {/* Open the full round on support.marbles.xyz — clip replay,
                          the provable-fairness card, and one-click independent verify. */}
                      <div className="info-entry">
                        <span className="label">{t('Full round')}:</span>
                        <a
                          className="value bet-history-support-link"
                          href={supportLookupUrl(bet)}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {t('View replay & verify ↗')}
                        </a>
                      </div>
                      {race && (
                        <div className="info-entry">
                          <span className="label">{t('Round')}:</span>
                          <span className="value">
                            {getRaceRoundId(race.round, game.track.shortName)}
                          </span>
                          <button
                            className="default-btn copy-btn"
                            title={
                              copiedStates[
                                `race-round-${getRaceRoundId(race.round, game.track.shortName)}`
                              ]
                                ? t('Copied!')
                                : t('Copy')
                            }
                            aria-label={
                              copiedStates[
                                `race-round-${getRaceRoundId(race.round, game.track.shortName)}`
                              ]
                                ? t('Copied!')
                                : t('Copy')
                            }
                            onClick={(e) => {
                              e.stopPropagation()
                              handleCopy(
                                getRaceRoundId(race.round, game.track.shortName),
                                `race-round-${getRaceRoundId(race.round, game.track.shortName)}`
                              )
                            }}
                          >
                            {copiedStates[
                              `race-round-${getRaceRoundId(race.round, game.track.shortName)}`
                            ] ? (
                              <CheckIcon className="copy-btn__icon copy-btn__icon--done" />
                            ) : (
                              <CopyIcon className="copy-btn__icon" />
                            )}
                          </button>
                        </div>
                      )}
                      <div className="info-entry">
                        <span className="label">{t('Bet Id')}:</span>
                        <span className="value">#{bet.id}</span>
                        <button
                          className="default-btn copy-btn"
                          title={copiedStates[`bet-${bet.id}`] ? t('Copied!') : t('Copy')}
                          aria-label={copiedStates[`bet-${bet.id}`] ? t('Copied!') : t('Copy')}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleCopy(bet.id, `bet-${bet.id}`)
                          }}
                        >
                          {copiedStates[`bet-${bet.id}`] ? (
                            <CheckIcon className="copy-btn__icon copy-btn__icon--done" />
                          ) : (
                            <CopyIcon className="copy-btn__icon" />
                          )}
                        </button>
                      </div>

                      <div className="info-entry">
                        <span className="label">{t('Bet Time')}:</span>
                        <span className="value">{formatDate(bet.createdAt, currentLang)}</span>
                        <button
                          className="default-btn copy-btn"
                          title={copiedStates[`time-${bet.id}`] ? t('Copied!') : t('Copy')}
                          aria-label={copiedStates[`time-${bet.id}`] ? t('Copied!') : t('Copy')}
                          onClick={(e) => {
                            e.stopPropagation()
                            handleCopy(bet.createdAt, `time-${bet.id}`)
                          }}
                        >
                          {copiedStates[`time-${bet.id}`] ? (
                            <CheckIcon className="copy-btn__icon copy-btn__icon--done" />
                          ) : (
                            <CopyIcon className="copy-btn__icon" />
                          )}
                        </button>
                      </div>
                      {/* Market label — only shown for chip-placement
                          side-bet bets (Even/Odd, Exact-Boundary). Main
                          market bets don't surface this since their
                          outcome row already carries the per-round
                          boundary label that fully describes the bet. */}
                      {(() => {
                        const m = marbleMarketMap.get(bet.betDetails.selectedItems[0] ?? '')
                        if (!m || m.marketType !== 'side_bet') return null
                        return (
                          <div className="info-entry">
                            <span className="label">{t('Market')}:</span>
                            <span className="value">{m.marketLabel}</span>
                          </div>
                        )
                      })()}
                      <div className="info-entry">
                        <span className="label">{t('Bet Amount')}:</span>
                        <span className="value">
                          {getCurrencySymbol(bet.currency)}
                          {displayMoney(bet.betAmount.toString())}
                        </span>
                      </div>
                      {/* EXPANDED row renders the FULL stored payout (last arg),
                          not the 2dp the collapsed row shows. Money is stored at
                          5-decimal wire precision and displayed truncated at 2, so
                          a 40.71978 payout reads "40.71" on screen while the
                          operator's wallet is credited 40.71978 — operators report
                          that as a discrepancy (SoftSwiss, DOP + SYP, Aug 2026).
                          There is room here, and this is where anyone reconciling
                          against the API looks. Fiat only; crypto is unchanged, and
                          clean amounts still render as "40.70". */}
                      <div className="info-entry">
                        <span className="label">{t('Won Amount')}:</span>
                        <span className="value">
                          {getCurrencySymbol(bet.currency)}
                          {getVisualPotentialPayout(
                            bet,
                            bet.status,
                            isCrashGameType(game.track.gameType),
                            true
                          )}
                        </span>
                      </div>
                      {/* Win multiplier was missing from history (QA-60158).
                          Derive the realised multiplier from the payout and
                          floor it (matching the placement display, QA-60088)
                          so it never reads higher than the payout. Up to 4dp
                          for accuracy, trailing zeros stripped so clean values
                          stay short (e.g. 7.33x, not 7.3300x). */}
                      {/* 🚨 NEVER derive a crash multiplier from potentialPayout:
                          for a crash bet that is the operator LIABILITY CEILING,
                          not stake x anything, so the ratio rendered 59523.8095x
                          on a 4.20 bet. Cashed out -> where the player got out;
                          otherwise -> where the round actually crashed, the same
                          number the collapsed badge shows. */}
                      {(() => {
                        const isCrash = isCrashGameType(game.track.gameType)
                        const value = isCrash
                          ? bet.cashoutMultiplier || crashMultiplierFor(race)
                          : bet.betAmount > 0 && bet.potentialPayout > 0
                            ? bet.potentialPayout / bet.betAmount
                            : undefined
                        if (!value || value <= 0) return null
                        return (
                          <div className="info-entry">
                            <span className="label">{t('Multiplier')}:</span>
                            <span className="value">{roundAmount(value, 4)}x</span>
                          </div>
                        )
                      })()}
                      <div className="info-entry">
                        <span className="label">{t('Outcome')}:</span>
                        {/* Humanised + translated — the raw wire status
                            leaked here ("cashed_out"). Unknown statuses
                            fall back to the raw value rather than blank. */}
                        <span className="value">
                          {bet.status === 'won'
                            ? t('Won')
                            : bet.status === 'lost'
                              ? t('Lost')
                              : bet.status === 'pending'
                                ? t('Pending')
                                : bet.status === 'cancelled'
                                  ? t('Cancelled')
                                  : bet.status === 'cashed_out'
                                    ? t('Cashed Out')
                                    : bet.status}
                        </span>
                      </div>
                      {/* Open the round's provably-fair modal (commitment /
                          revealed seed / derivation) for settled rounds — same
                          modal as the history pills. Gated on the ROUND having
                          a commitment or a reveal, not on the game family: the
                          old isCrashGameType gate left every counting (CCTV)
                          round without an opener, so the modal below could
                          never resolve for one. */}
                      {race && raceEnded && hasRoundFairness(race) && (
                        <div className="info-entry info-entry--fairness">
                          <button
                            className="default-btn bet-history-fairness-btn"
                            onClick={(e) => {
                              e.stopPropagation()
                              setFairnessRaceId(race.uuid)
                            }}
                          >
                            <span>{t('Provably fair')}</span>
                          </button>
                        </div>
                      )}
                    </div>
                    {isCrashGameType(game.track.gameType) ? (
                      <div className={`bottom-bar ${bet.betDetails.type}`}>
                        <CrashBetHistoryResult bet={bet} race={race} raceEnded={raceEnded} t={t} />
                      </div>
                    ) : isCCTVGameType(game.track.gameType) ? (
                      <div className="bottom-bar bottom-bar--irl-alt-history">
                        {(() => {
                          const pickedMarble = game.marbles.find(
                            (m: Marble) => m.uuid === bet.betDetails.selectedItems[0]
                          )
                          // Resolve the bet's market — when the picked
                          // marble belongs to a configured side-bet
                          // market (Even/Odd, Exact-Boundary), the
                          // settled winner lives in
                          // `payload.marketResults[marketId]` instead of
                          // the main `payload.results`. Falls through
                          // to the main-market branch when the marble
                          // is on the main market or when gameConfig
                          // hasn't loaded markets (classic-mode bets,
                          // pre-deploy rounds).
                          const market = pickedMarble
                            ? marbleMarketMap.get(pickedMarble.uuid)
                            : undefined
                          const isSideBetBet = market?.marketType === 'side_bet'

                          const winnerResult =
                            isSideBetBet && market
                              ? raceSettledEvent?.payload.marketResults?.[market.marketId]?.find(
                                  (r) => r.position === 1
                                )
                              : raceSettledEvent?.payload.results.find((r) => r.position === 1)
                          const winnerMarble = winnerResult
                            ? game.marbles.find((m: Marble) => m.uuid === winnerResult.marbleId)
                            : undefined
                          // Per-round boundary labels (e.g. "Under 12" /
                          // "13-15" / "Over 16" / "12 or 16") — preferred
                          // over the abstract option name so the player
                          // sees the actual numeric outcome they bet on.
                          // Falls back to the colour name on pre-deploy
                          // CCTV rounds where the engine hadn't started
                          // attaching optionLabels yet.
                          const cctvOptionLabels: Record<string, string> =
                            race?.raceEvents.find(isRaceStartedEvent)?.payload?.optionLabels ?? {}
                          const pickedLabel = pickedMarble
                            ? (cctvOptionLabels[pickedMarble.uuid] ??
                              getTranslatedMarbleColor(pickedMarble.color, t))
                            : '—'
                          // Outcome label fallback: when the round IS
                          // settled but the side-bet's market doesn't
                          // appear in `marketResults` (Exact-Boundary
                          // on non-jackpot rounds is the common case —
                          // engine intentionally omits the entry), the
                          // bet was settled as a loss via the lost-bets
                          // sweep. Prefer showing the actual count
                          // (engine attaches `finalCount` on settle)
                          // since "the count was 13, you bet Exact 12"
                          // tells the player WHY they lost. Falls back
                          // to "No winner" on pre-deploy rounds where
                          // the engine hadn't started attaching count.
                          // True "Pending" is reserved for rounds that
                          // genuinely haven't reached race_settled yet.
                          const finalCount = raceSettledEvent?.payload.finalCount
                          const resultLabel = winnerResult
                            ? (cctvOptionLabels[winnerResult.marbleId] ??
                              getTranslatedMarbleColor(winnerResult.marbleColor, t))
                            : raceSettledEvent && isSideBetBet
                              ? typeof finalCount === 'number'
                                ? String(finalCount)
                                : t('No winner')
                              : t('Pending')
                          const isCorrectPick =
                            pickedMarble &&
                            winnerResult &&
                            pickedMarble.uuid === winnerResult.marbleId

                          return (
                            <>
                              <div className="irl-alt-history-row">
                                <span className="irl-alt-history-label">{t('Outcome')}</span>
                                <div className="irl-alt-history-right">
                                  <span
                                    className={`irl-alt-history-value ${winnerResult ? 'result' : 'pending'}`}
                                  >
                                    {resultLabel}
                                  </span>
                                  {winnerMarble && (
                                    <MarbleItem
                                      className={`marble-${winnerMarble.color}`}
                                      marble={winnerMarble}
                                    />
                                  )}
                                </div>
                              </div>
                              <div className="irl-alt-history-divider" />
                              <div
                                className={`irl-alt-history-row pick-row ${isCorrectPick ? 'correct' : ''}`}
                              >
                                <span className="irl-alt-history-label">{t('Your Pick')}</span>
                                <div className="irl-alt-history-right">
                                  <span
                                    className={`irl-alt-history-value pick ${isCorrectPick ? 'correct' : ''}`}
                                  >
                                    {pickedLabel}
                                  </span>
                                  {pickedMarble && (
                                    <MarbleItem
                                      className={`marble-${pickedMarble.color}`}
                                      marble={pickedMarble}
                                    />
                                  )}
                                </div>
                              </div>
                            </>
                          )
                        })()}
                      </div>
                    ) : (
                      <div className={`bottom-bar ${bet.betDetails.type}`}>
                        <BetTypeLabel betType={bet.betDetails.type} className="label" />
                        <div className="marbles">
                          {renderBetMarbles(bet, raceSettledEvent, raceEnded)}
                        </div>
                      </div>
                    )}
                  </div>
                )
              })
            ) : (
              <div className="entry entry--empty">
                <span>{t('No bet history found')}</span>
              </div>
            )}

            {hasMoreBets && (
              <button
                className="default-btn h-6 block mt-2"
                onClick={loadMoreBets}
                disabled={isLoading}
              >
                <span>{isLoading ? t('Loading...') : t('Load More')}</span>
              </button>
            )}
          </div>
        </div>
      </div>
      {(() => {
        // Round-fairness modal (crash, counting, marble or Birdie — the family
        // is read off the reveal) — resolves against the Supabase-fetched
        // races; self-closes when the id stops resolving.
        const fairnessRace = resolveModalRace(races, fairnessRaceId)
        return fairnessRace ? (
          <CrashRoundModal race={fairnessRace} onClose={() => setFairnessRaceId(null)} />
        ) : null
      })()}
    </div>
  )
}
